import React from 'react';
import { Box, Stack } from '@mui/material';

export interface StatusTrackerProps {
  status: string;
  showLegend?: boolean;
}

const stages = [
  { key: 'I', label: 'Invited', status: 'Invited', color: '#6366f1' },
  { key: 'S', label: 'Scheduled', status: 'Scheduled', color: '#8b5cf6' },
  { key: 'C', label: 'Confirmed', status: 'Confirmed', color: '#3b82f6' },
  { key: 'L', label: 'Live', status: 'Live', color: '#10b981' },
  { key: 'F', label: 'Completed', status: 'Completed', color: '#22c55e' },
];

const getStageStyles = (currentStatus: string, stageStatus: string) => {
  if (currentStatus === 'Completed') {
    return {
      background: 'linear-gradient(to right, #22c55e, #16a34a)',
      color: '#fff',
      boxShadow: '0 4px 6px -1px rgba(34, 197, 94, 0.3)',
    };
  }

  const stageOrder = stages.findIndex((s) => s.status === stageStatus);
  const currentStageOrder = stages.findIndex((s) => s.status === currentStatus);

  if (stageOrder <= currentStageOrder) {
    switch (stageStatus) {
      case 'Invited':
        return {
          background: 'linear-gradient(to right, #6366f1, #4f46e5)',
          color: '#fff',
          boxShadow: '0 4px 6px -1px rgba(99, 102, 241, 0.3)',
        };
      case 'Scheduled':
        return {
          background: 'linear-gradient(to right, #8b5cf6, #7c3aed)',
          color: '#fff',
          boxShadow: '0 4px 6px -1px rgba(139, 92, 246, 0.3)',
        };
      case 'Confirmed':
        return {
          background: 'linear-gradient(to right, #3b82f6, #2563eb)',
          color: '#fff',
          boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.3)',
        };
      case 'Live':
        return {
          background: 'linear-gradient(to right, #10b981, #059669)',
          color: '#fff',
          boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.3)',
        };
      case 'Completed':
        return {
          background: 'linear-gradient(to right, #22c55e, #16a34a)',
          color: '#fff',
          boxShadow: '0 4px 6px -1px rgba(34, 197, 94, 0.3)',
        };
      default:
        return {
          background: 'linear-gradient(to right, #f3f4f6, #e5e7eb)',
          color: '#6b7280',
          boxShadow: 'none',
        };
    }
  }
  return {
    background: 'linear-gradient(to right, #f3f4f6, #e5e7eb)',
    color: '#6b7280',
    boxShadow: 'none',
  };
};

const getConnectorColor = (currentStatus: string, stageStatus: string) => {
  if (currentStatus === 'Completed') {
    return 'linear-gradient(to right, #4ade80, #22c55e)';
  }

  const stageOrder = stages.findIndex((s) => s.status === stageStatus);
  const currentStageOrder = stages.findIndex((s) => s.status === currentStatus);

  if (stageOrder < currentStageOrder) {
    switch (currentStatus) {
      case 'Scheduled':
        return 'linear-gradient(to right, #a78bfa, #8b5cf6)';
      case 'Confirmed':
        return 'linear-gradient(to right, #60a5fa, #3b82f6)';
      case 'Live':
        return 'linear-gradient(to right, #34d399, #10b981)';
      case 'Completed':
        return 'linear-gradient(to right, #4ade80, #22c55e)';
      default:
        return 'linear-gradient(to right, #818cf8, #6366f1)';
    }
  }
  return 'linear-gradient(to right, #e5e7eb, #d1d5db)';
};

export const StatusTracker: React.FC<StatusTrackerProps> = ({
  status = 'unknown',
}) => {
  return (
    <Stack spacing={1.5}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {stages.map((stage, index) => {
          const isCurrent =
            status === stage.status && !['unknown', 'Completed'].includes(status);
          const stageStyle = getStageStyles(status, stage.status);

          return (
            <Box key={stage.key} sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  transition: 'all 0.2s',
                  outline: isCurrent ? `2px solid ${stage.color}` : 'none',
                  outlineOffset: isCurrent ? '1px' : '0px',
                  ...stageStyle,
                }}
              >
                {stage.key}
              </Box>
              {index < stages.length - 1 && (
                <Box
                  sx={{
                    height: 2,
                    width: 12,
                    mx: 0.5,
                    background: getConnectorColor(status, stage.status),
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Stack>
  );
};

export default StatusTracker;
